// What a new password may not be, beyond its length (DIRECTION A27).
//
// ONE QUESTION: IS IT AMONG THE FIRST A STRANGER WOULD TRY? A league here is
// casual and its accounts hold little, so the rules stay light: no mix of
// letters, digits and symbols to make up (that pushes people to `Passw0rd!`
// and buys nothing), only a refusal of the passwords every guessing list
// starts with, which is the check NIST's guidance on passwords (SP 800-63B)
// asks for: values from lists of common and breached passwords, repeated or
// sequential characters, and words from the context, such as the username.
// So, refused:
//
//   - the passwords at the top of the published lists, as typed: `password1`,
//     `qwerty123`, `iloveyou`, `1q2w3e4r`;
//   - one of their words with digits or symbols before or after it, and the
//     usual swaps of letters for look-alikes: `Password2024!`, `p@ssw0rd`;
//   - one key or a short run of keys over and over: `aaaaaaaa`, `12121212`;
//   - a straight run up or down: `12345678`, `abcdefgh`, `0987654321`;
//   - at sign-up, the email it is made with or the part before its `@`, with
//     digits or not (the one request that carries the email beside it).
//
// Only a NEW password is held to this: signing up, a reset, a change, the
// undo of an email change. Signing in never is, so nobody whose password
// predates the rule is locked out of their account by it.
//
// The words are from the published lists of the most used passwords; a
// sport's own words (a club's name) are not added here, since the core never
// names a sport.

/** The sentence a refusal says, for every client alike. */
export const COMMON_PASSWORD_MESSAGE =
  'That one is too common, so it is among the first a stranger would try. A few words of your own together are easy to remember and hard to guess.';

/** The sentence for a password built from the account's own email. */
export const EMAIL_PASSWORD_MESSAGE =
  'That is too close to your email, which is one of the first things a stranger would try. A few words of your own together are easy to remember and hard to guess.';

/** Whole passwords from the lists that the rules below do not already catch. */
const COMMON = new Set([
  '1q2w3e4r',
  '1q2w3e4r5t',
  '1q2w3e4r5t6y',
  '1qaz2wsx',
  '1qaz2wsx3edc',
  '1qazxsw2',
  '!qaz2wsx',
  'zaq12wsx',
  'zaq1xsw2',
  'q1w2e3r4',
  'q1w2e3r4t5',
  'q1w2e3r4t5y6',
  'qazwsxedc',
  'qweasdzxc',
  'a1b2c3d4',
  'a1b2c3d4e5',
  '1a2b3c4d',
  '11223344',
  '112233445566',
  '12341234',
  '123123123',
  '123321123',
  '12344321',
  '123454321',
  '1234554321',
  '12345678910',
  '147258369',
  '147852369',
  '123698745',
  '741852963',
  '963852741',
  '789456123',
  '159357456',
  '1qaz@wsx',
]);

/**
 * Words that are common with anything tacked on: digits or symbols before or
 * after, and look-alike swaps undone first. Compared to the whole of what is
 * left, so a passphrase that merely contains one (`myfootballteam`) is fine.
 */
const WORDS = new Set([
  'password',
  'passwort',
  'passwd',
  'pass',
  'mypassword',
  'newpassword',
  'qwerty',
  'qwertyui',
  'qwertyuiop',
  'qwer',
  'qwe',
  'qwertz',
  'azerty',
  'asdf',
  'asdfgh',
  'asdfghjk',
  'asdfghjkl',
  'zxcvbn',
  'zxcvbnm',
  'qazwsx',
  'qweasd',
  'a',
  'aa',
  'abc',
  'abcd',
  'abcde',
  'abcdef',
  'abcdefg',
  'iloveyou',
  'iloveu',
  'loveyou',
  'love',
  'lovely',
  'welcome',
  'letmein',
  'admin',
  'administrator',
  'root',
  'login',
  'user',
  'guest',
  'test',
  'default',
  'changeme',
  'secret',
  'master',
  'monkey',
  'dragon',
  'shadow',
  'sunshine',
  'princess',
  'superman',
  'batman',
  'spiderman',
  'starwars',
  'pokemon',
  'minecraft',
  'football',
  'baseball',
  'basketball',
  'soccer',
  'hockey',
  'freedom',
  'whatever',
  'trustno',
  'trustnoone',
  'hello',
  'helloworld',
  'charlie',
  'michael',
  'jordan',
  'jennifer',
  'ashley',
  'jessica',
  'daniel',
  'thomas',
  'hunter',
  'ranger',
  'buster',
  'tigger',
  'maggie',
  'ginger',
  'pepper',
  'summer',
  'flower',
  'cookie',
  'cheese',
  'chocolate',
  'butterfly',
  'computer',
  'internet',
  'google',
  'samsung',
  'apple',
  'iphone',
  'facebook',
  'killer',
  'mustang',
  'harley',
  'liverpool',
  'chelsea',
  'arsenal',
  'yankees',
  'lakers',
  'cowboys',
  'steelers',
  'eagles',
  'packers',
]);

/** The swaps people make for letters: `p@ssw0rd` is `password`. */
const LOOK_ALIKES: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '@': 'a',
  $: 's',
  '!': 'i',
};

/** What is left of a password with the digits and symbols around it taken off. */
function core(lower: string): string {
  return lower.replace(/^[^a-z]+/, '').replace(/[^a-z]+$/, '');
}

function undoLookAlikes(text: string): string {
  return [...text].map((c) => LOOK_ALIKES[c] ?? c).join('');
}

/** One key, or a run of up to four, over and over: `aaaaaaaa`, `12121212`, `abcabcabc`. */
function repeats(lower: string): boolean {
  return /^(.{1,4})\1+$/.test(lower);
}

/** The step from one key to the next, with `9` then `0` counted as the next digit. */
function step(a: string, b: string): number {
  if (a === '9' && b === '0') return 1;
  if (a === '0' && b === '9') return -1;
  return b.charCodeAt(0) - a.charCodeAt(0);
}

/** A straight run up or down the digits or the alphabet: `12345678`, `hgfedcba`. */
function isRun(lower: string): boolean {
  if (lower.length < 2) return false;
  const first = step(lower[0]!, lower[1]!);
  if (Math.abs(first) !== 1) return false;
  for (let i = 2; i < lower.length; i++) {
    if (step(lower[i - 1]!, lower[i]!) !== first) return false;
  }
  return true;
}

/** Whether a password is among the first a stranger would try, whatever the account. */
export function isCommonPassword(password: string): boolean {
  const lower = password.toLowerCase();
  if (COMMON.has(lower) || repeats(lower) || isRun(lower)) return true;
  return readings(lower).some((w) => WORDS.has(w));
}

/**
 * Each way a password can be read as a word with something tacked on. The
 * letters in the middle are the word; of the digits and symbols around them,
 * the ones next to the letters may be look-alikes belonging to it (`$unshine1`,
 * `123secre7`) or may be tacked on, so every split is read, each with the
 * swaps undone. A password with no letters at all has no word to read, and
 * none is made up from its digits (`12345674` is not `a`).
 */
function readings(lower: string): string[] {
  const lead = /^[^a-z]*/.exec(lower)![0];
  const middle = core(lower);
  if (middle.length === 0) return [];
  const trail = lower.slice(lead.length + middle.length);
  // A look-alike belongs to the word only beside it, so a few characters
  // each side are enough, and a long run of digits costs no more to read.
  const out: string[] = [];
  for (let i = 0; i <= Math.min(lead.length, 3); i++) {
    for (let j = 0; j <= Math.min(trail.length, 3); j++) {
      out.push(undoLookAlikes(lead.slice(lead.length - i) + middle + trail.slice(0, j)));
    }
  }
  return out;
}

/** Whether a password is the account's email, or the part before its `@`, give or take digits. */
export function isEmailPassword(password: string, email: string): boolean {
  const lower = password.toLowerCase();
  const address = email.trim().toLowerCase();
  if (address.length === 0) return false;
  if (lower === address) return true;
  const local = address.split('@')[0] ?? '';
  const name = core(local);
  // A short local part (`jo@`) is too little to call a password built from it.
  return name.length >= 4 && core(lower) === name;
}

/**
 * Why a new password is refused, or null when it is not: the sentence each
 * client shows, and the server's refusal (`NewPassword`, `SignupRequest`).
 * The length is the schema's own rule (`PASSWORD_MIN_LENGTH`), checked first.
 */
export function newPasswordProblem(password: string, email?: string | null): string | null {
  if (email && isEmailPassword(password, email)) return EMAIL_PASSWORD_MESSAGE;
  if (isCommonPassword(password)) return COMMON_PASSWORD_MESSAGE;
  return null;
}
